import RequestTabs from '~/components/shared/RequestTabs';
import trace from '~/lib/trace';
import React, {useState, useEffect, useLayoutEffect, useContext} from 'react';
import {View} from 'react-native';
import {Divider} from 'react-native-paper';
import {useIsFocused} from '@react-navigation/native';
import {HeaderBackButton} from '@react-navigation/elements';
import sy from '~/styles';
import withProtectedScreen from '~/components/hoc/with-protected-screen';
import withServiceRequestShortcuts from '~/components/hoc/with-service-request-shortcuts';
import {
  Text,
  ServiceRequestStatusIcon,
  TabBar,
  MoreMenu,
} from '~/components/controls';
import {SyncErrorsDialog} from '~/components/shared/Dialogs';
import Format from '~/lib/format';
import {service_request as serviceRequestApi} from '~/api/private';
import {STATUS_CODES} from '~/types/statuses';
import {Tabs} from '~/types/tabs';
import {useDispatch} from '~/lib/hooks';
import {addToRecents} from '~/actions';
import ChevronLeftIcon from '~/images/md-icons/chevron_left/materialicons/24px.svg';
import UploadIcon from '~/images/md-icons/upload/materialsymbolsoutlined/24px.svg';
import ReplayIcon from '~/images/md-icons/replay/materialsymbolsoutlined/24px.svg';
import ServiceRequestContext from './context';
import withServiceRequestContext from './components/withServiceRequestContext';
import DamageTab from './components/DamageTab';
import MessagesTab from './components/MessagesTab';
import PropTypes from 'prop-types';
const renderChevronLeftIcon = () => <ChevronLeftIcon />;
const renderAutolineIcon = () => <UploadIcon fill="#4a4a49" />;
const renderCancelledIcon = () => (
  <ServiceRequestStatusIcon fill="#4a4a49" status={STATUS_CODES.Cancelled} />
);
const renderReplayIcon = () => <ReplayIcon fill="#4a4a49" />;
const Index = ({navigation, route}) => {
  const initialTab = route.params?.tab ?? Tabs.Request;
  const [selectedTab, setSelectedTab] = useState(initialTab);
  const [syncErrors, setSyncErrors] = useState(null);
  const {damage_id, loading, overview, readLastMessage, has_autoline, refresh} =
    useContext(ServiceRequestContext);
  const hasUnreadMessages = readLastMessage === false;
  const isFocused = useIsFocused();
  const dispatch = useDispatch();
  const menuItems = [
    has_autoline &&
      [
        STATUS_CODES.CheckIn,
        STATUS_CODES.Reserved,
        STATUS_CODES.Approved,
        STATUS_CODES.Done,
      ].includes(overview?.status) && {
        title: 'Export naar Autoline',
        leadingIcon: renderAutolineIcon,
        onPress: async () => {
          const {success, result} = await serviceRequestApi.sync(
            damage_id,
            'autoline',
          );
          if (success) {
            let errors = [];
            for (const task of result) {
              if (!task.keyloop_id) {
                errors = [...errors, task.description];
              }
              errors = [
                ...errors,
                ...task.parts
                  .filter((item) => !item.keyloop_id)
                  .map((item) => item.description),
              ];
            }
            if (errors.length > 0) {
              setSyncErrors(errors);
            }
          }
          refresh();
        },
      },
    [STATUS_CODES.Reserved, STATUS_CODES.CheckIn].includes(
      overview?.status,
    ) && {
      title: 'Opdracht annuleren',
      leadingIcon: renderCancelledIcon,
      onPress: async () => {
        await serviceRequestApi.change_status(
          damage_id,
          STATUS_CODES.Cancelled,
        );
        await refresh();
      },
    },
    overview?.status === STATUS_CODES.Done && {
      title: 'Terug naar Goedkeuring',
      leadingIcon: renderReplayIcon,
      onPress: async () => {
        await serviceRequestApi.change_status(damage_id, STATUS_CODES.CheckIn);
        await refresh();
      },
    },
  ].filter(Boolean);
  const onHeaderBackPress = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.replace('Request', {
        request_id: overview?.parent_id,
      });
    }
  };
  const renderHeaderLeft = () => (
    <HeaderBackButton
      backImage={renderChevronLeftIcon}
      onPress={onHeaderBackPress}
    />
  );
  const renderHeaderTitle = () => {
    if (loading) {
      return null;
    }
    return (
      <View>
        <Text style={sy.largePlus}>
          Schade&nbsp;&middot;&nbsp;
          {Format.license(overview?.license)}
        </Text>
      </View>
    );
  };
  const renderHeaderRight = () => {
    if (loading) {
      return null;
    }
    return (
      <View
        style={[sy['flex-row'], sy['items-center'], sy['gap-2'], sy['mr-4']]}>
        <ServiceRequestStatusIcon status={overview.status} />
        {menuItems.length > 0 && <MoreMenu items={menuItems} />}
      </View>
    );
  };
  useLayoutEffect(() => {
    navigation.setOptions({
      headerShown: true,
      headerLeft: renderHeaderLeft,
      headerStatusBarHeight: 0,
      headerTitleAlign: 'center',
      headerStyle: {
        borderBottomColor: 'transparent',
        boxShadow: 'none',
      },
      headerTitle: renderHeaderTitle,
      headerRight: renderHeaderRight,
    });
  }, [navigation, overview, loading]);
  useEffect(() => {
    const fetch = async () => {
      await refresh();
    };
    if (isFocused) {
      fetch().catch(trace);
      dispatch(addToRecents(damage_id));
    }
  }, [damage_id, refresh, isFocused, dispatch]);
  useEffect(() => {
    navigation.setParams({
      tab: selectedTab,
    });
  }, [navigation, selectedTab]);
  return (
    <View style={sy.mainView}>
      {!loading && (
        <>
          <RequestTabs
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            hasUnreadMessages={hasUnreadMessages}
            Tabs={Tabs}
            TabBar={TabBar}
          />
          <Divider style={sy['bg-gray88']} />
          <TabBar.Content tab={selectedTab}>
            <DamageTab tab={Tabs.Request} onChangeTab={setSelectedTab} />
            <MessagesTab tab={Tabs.Messages} onChangeTab={setSelectedTab} />
          </TabBar.Content>
          <SyncErrorsDialog
            visible={syncErrors?.length > 0}
            title="Export naar Autoline"
            body="De export naar Autoline is afgerond. De volgende items konden niet worden geëxporteerd."
            errors={syncErrors}
            onDismiss={() => setSyncErrors(null)}
          />
        </>
      )}
    </View>
  );
};
Index.propTypes = {
  navigation: PropTypes.any,
  route: PropTypes.any,
};
export default withProtectedScreen(
  withServiceRequestShortcuts(
    withServiceRequestContext(Index),
    (props) => props.route.params?.damage_id,
  ),
);
